import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { setCanonical, setMeta } from '@/utils/seo';
import { FAQSchema, BreadcrumbSchema, SpeakableSchema, ServiceSchema } from '@/components/SEOSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITY_FAQS = (name) => [
  { question: `What estate planning services are available in ${name}?`, answer: `LegaSecure offers digital estate planning services accessible from ${name}, including Online SPA (Special Power of Attorney), Guardian Mandates for children, and an encrypted Digital Vault. All services are available online at legasecure.com.` },
  { question: `How can I create an SPA in ${name}?`, answer: `You can create an Apostille-ready Special Power of Attorney through LegaSecure's online platform without visiting a notary in ${name}. The Online SPA authorizes your representative to handle property, banking, and legal matters on your behalf.` },
  { question: `Do I need a lawyer for estate planning in ${name}?`, answer: `While consulting a lawyer is recommended for complex matters, LegaSecure provides tools to help you organize your estate plan from ${name}. You can store documents, create guardian mandates, and generate Online SPAs through the platform.` },
  { question: `How do I protect my property in ${name} while working abroad?`, answer: `Use LegaSecure's Online SPA to authorize a trusted family member in ${name} to manage your property. Store your land titles and documents in the Zero-Knowledge Digital Vault for safekeeping.` },
];

export default function CityPage() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/cities/${slug}`).then(r => {
      setCity(r.data);
      setCanonical(`/city/${slug}`);
      setMeta(`Estate Planning in ${r.data.name} | LegaSecure Guide`, `Find estate planning services in ${r.data.name}, ${r.data.province}. Online SPA, guardian mandates, and digital legacy vault for ${r.data.name} residents and OFWs.`);
    }).catch(() => setCity(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="ls-container py-20 text-center"><i className="fa-solid fa-spinner fa-spin text-2xl text-[var(--ls-navy)]"></i></div>;
  if (!city) return <div className="ls-container py-20 text-center"><h1 className="text-2xl font-bold">City not found</h1><Link to="/all-cities" className="ls-btn-gold mt-4 inline-block">Browse All Cities</Link></div>;

  const faqs = CITY_FAQS(city.name);

  return (
    <div data-testid="city-page">
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Cities', url: '/all-cities' }, { name: city.name }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <ServiceSchema />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <nav className="text-sm text-[var(--ls-text-light)] mb-4">
            <Link to="/" className="hover:text-[var(--ls-gold)]">Home</Link> / <Link to="/all-cities" className="hover:text-[var(--ls-gold)]">Cities</Link> / <span className="text-[var(--ls-navy)] font-medium">{city.name}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>Estate Planning Services in {city.name}</h1>
          <p className="speakable-intro text-lg max-w-2xl" style={{ color: '#4A5568' }}>
            You've worked hard for your family in {city.name} — don't let the unexpected put that at risk. Whether you're here or working abroad, LegaSecure helps you protect what matters most with Online SPA, Guardian Mandates, and a secure Digital Vault.
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            <a href="https://legasecure.com" className="ls-btn-navy">Get Started Free</a>
            <a href="https://legasecure.com/request-information" className="ls-btn-outline">Request Information</a>
          </div>
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container">
          <div className="ls-card mb-8 bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800">
              <i className="fa-solid fa-heart mr-2"></i>
              <strong>Imagine this:</strong> You're an OFW working abroad, and your child in {city.name} gets sick. Your family needs to access your bank account for hospital bills, but no one has the authority. Or worse — what if something happens to you? Who takes care of your kids? Who manages your property? <strong>LegaSecure exists so your family never has to face these questions alone.</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: 'fa-file-signature', title: `No-Flight Permission`, desc: `Don't fly home just to sign papers. Create an Apostille-ready SPA from anywhere and let your trusted person in ${city.name} handle property, banking, and legal matters for you.`, link: '/guides/special-power-of-attorney-philippines' },
              { icon: 'fa-user-shield', title: `Your Children's Safety Net`, desc: `If something happens to you, who picks up your kids from school in ${city.name}? A Guardian Mandate gives your chosen person instant authority — no court needed.`, link: '/guides/guardian-mandate-filipino-children' },
              { icon: 'fa-vault', title: `Your Family's Vault`, desc: `Land titles, bank details, insurance — all encrypted and accessible only by you and the people you trust. Not even LegaSecure can see what's inside.`, link: '/guides/digital-legacy-planning' },
            ].map((s, i) => (
              <Link key={i} to={s.link} className="ls-card group">
                <i className={`fa-solid ${s.icon} text-[var(--ls-gold)] text-2xl mb-3`}></i>
                <h3 className="font-bold mb-2 group-hover:text-[var(--ls-gold)]">{s.title}</h3>
                <p className="text-sm text-[var(--ls-text-light)]">{s.desc}</p>
                <p className="text-[var(--ls-gold)] text-sm font-medium mt-3">Learn how <i className="fa-solid fa-arrow-right ml-1"></i></p>
              </Link>
            ))}
          </div>

          <div className="ls-card mb-12 prose-content">
            <h2>Your Family in {city.name} Deserves a Plan</h2>
            <p>Whether you live in {city.name} or are an OFW with loved ones here, the truth is simple: life is unpredictable. {city.name} is in {city.province}, {city.region}, home to {city.population} people — families just like yours who depend on someone to keep things together.</p>
            <p>Without a plan, your family could face frozen bank accounts, property disputes that take years to settle, and the heartbreak of not knowing what you wanted. LegaSecure gives them clarity instead of chaos.</p>
            <h2>What Families in {city.name} Use LegaSecure For</h2>
            <ul>
              <li><strong>Property Transfer:</strong> Selling or transferring land in {city.name} without flying home — your SPA-authorized representative handles everything.</li>
              <li><strong>Bank Account Access:</strong> Making sure your family can access funds for emergencies, tuition, and daily needs.</li>
              <li><strong>Child Protection:</strong> Naming who takes care of your children, with detailed instructions they can follow immediately.</li>
              <li><strong>Document Safety:</strong> Keeping land titles, insurance policies, and passwords in an encrypted vault — accessible to the right people at the right time.</li>
            </ul>
          </div>

          {/* How It Works - Mini */}
          <div className="ls-card mb-12 bg-[var(--ls-bg)]">
            <h2 className="text-xl font-bold mb-6 text-center">It's Easier Than You Think</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="w-10 h-10 rounded-full bg-[var(--ls-gold)] text-white font-bold flex items-center justify-center mx-auto mb-2">1</div>
                <p className="text-sm font-medium text-[var(--ls-navy)]">Sign Up Free</p>
                <p className="text-xs text-[var(--ls-text-light)]">No credit card needed</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-[var(--ls-gold)] text-white font-bold flex items-center justify-center mx-auto mb-2">2</div>
                <p className="text-sm font-medium text-[var(--ls-navy)]">Upload Documents</p>
                <p className="text-xs text-[var(--ls-text-light)]">Encrypted & secure</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-[var(--ls-gold)] text-white font-bold flex items-center justify-center mx-auto mb-2">3</div>
                <p className="text-sm font-medium text-[var(--ls-navy)]">Protect Your Family</p>
                <p className="text-xs text-[var(--ls-text-light)]">Peace of mind, anywhere</p>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <FAQItem key={i} question={f.question} answer={f.answer} />
              ))}
            </div>
          </div>

          {city.nearby_cities?.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Nearby Cities in {city.region}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {city.nearby_cities.map(c => (
                  <Link key={c.slug} to={`/city/${c.slug}`} className="ls-card p-4 flex items-center gap-3">
                    <i className="fa-solid fa-city text-[var(--ls-gold)]"></i>
                    <div>
                      <p className="font-semibold text-sm">{c.name}</p>
                      <p className="text-xs text-[var(--ls-text-light)]">{c.province}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">OFW from {city.name}? Find Help in Your Country</h2>
            <p className="text-sm text-[var(--ls-text-light)] mb-4">If you're from {city.name} and working abroad, find estate planning help specific to your country.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'Saudi Arabia', slug: 'saudi-arabia' },
                { name: 'UAE', slug: 'united-arab-emirates' },
                { name: 'United States', slug: 'united-states' },
                { name: 'Canada', slug: 'canada' },
                { name: 'Japan', slug: 'japan' },
                { name: 'Singapore', slug: 'singapore' },
                { name: 'Australia', slug: 'australia' },
                { name: 'United Kingdom', slug: 'united-kingdom' },
              ].map(c => (
                <Link key={c.slug} to={`/ofw/${c.slug}`} className="ls-card p-3 flex items-center gap-2 text-sm hover:border-[var(--ls-gold)] border border-transparent">
                  <i className="fa-solid fa-globe text-[var(--ls-gold)]"></i>
                  <span className="text-[var(--ls-navy)] font-medium">{c.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--ls-navy)] py-12">
        <div className="ls-container text-center">
          <h2 className="text-2xl font-bold text-white mb-3 font-['Crimson_Pro']">Your family in {city.name} is counting on you.</h2>
          <p className="text-white/70 mb-6 max-w-lg mx-auto">It takes less than 5 minutes to start. Give them the peace of mind they deserve — no matter where you are.</p>
          <a href="https://legasecure.com" className="ls-btn-gold text-lg px-8 py-3">Start Your Free Plan <i className="fa-solid fa-arrow-right ml-2"></i></a>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ls-card cursor-pointer" onClick={() => setOpen(!open)}>
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-sm text-[var(--ls-navy)] pr-4">{question}</h3>
        <i className={`fa-solid fa-chevron-down text-[var(--ls-text-light)] transition-transform text-xs ${open ? 'rotate-180' : ''}`}></i>
      </div>
      {open && <p className="mt-2 text-sm text-[var(--ls-text-light)] leading-relaxed">{answer}</p>}
    </div>
  );
}
